import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, errorMessage } from "@/app/api";
import { safeNext, useAuthConfig, useLogin, useMe } from "@/app/auth";
import { Brand } from "@/app/Brand";
import { Turnstile } from "@/app/Turnstile";
import { Alert, Button, Field, Form, Input, Login } from "@/ui";

/** 登录页（设计系统 Login，docs/04 第 9 节）。启用人机验证时要先过 Turnstile（D173）。成功后回到登录前要去的页面。 */
export function LoginPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const me = useMe();
  const config = useAuthConfig();
  const login = useLogin();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [widgetError, setWidgetError] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);

  if (me.data) return <Navigate to={next} replace />;

  const siteKey = config.data?.turnstile_site_key ?? "";
  // 登录配置读到之前（含读取失败）不许提交，免得启用了人机验证却没带令牌
  const ready = config.isSuccess && !!username && !!password && (!siteKey || !!token);
  const submit = () => {
    if (!ready || login.isPending) return;
    login.mutate(
      { username, password, ...(siteKey && token ? { turnstile_token: token } : {}) },
      {
        onSuccess: () => navigate(next, { replace: true }),
        // 令牌只能用一次，无论哪种失败都要重新验证
        onError: () => { if (siteKey) setResetKey((k) => k + 1); },
      },
    );
  };
  const failure = login.error ? loginFailure(login.error) : null;
  const onToken = (t: string | null) => {
    setToken(t);
    if (t) setWidgetError(null);
  };

  return (
    <Login brand={<Brand />} title="登录管理后台" onSubmit={submit}>
      {failure && <Alert tone="danger" title={failure} />}
      {widgetError && <Alert tone="danger" title={widgetError} />}
      {config.isError && <Alert tone="danger" title={`读不到登录配置，请刷新页面重试：${errorMessage(config.error)}`} />}
      <Form>
        <Field label="用户名" htmlFor="login-username">
          <Input id="login-username" autoComplete="username" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} />
        </Field>
        <Field label="密码" htmlFor="login-password">
          <Input id="login-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
      </Form>
      {siteKey && <Turnstile siteKey={siteKey} onToken={onToken} onError={setWidgetError} resetKey={resetKey} />}
      <Button variant="primary" type="submit" disabled={login.isPending || !ready}>{login.isPending ? "登录中" : "登录"}</Button>
    </Login>
  );
}

/** 失败说明：400（人机验证未通过）、401（用户名或密码错误）、403（来源不在白名单）、429（次数过多）、503（人机验证服务不可用）
 * 用服务端的文案，其余说明是服务问题。 */
function loginFailure(e: unknown): string {
  if (e instanceof ApiError && [400, 401, 403, 429, 503].includes(e.status)) return e.message;
  return `登录暂时不可用：${errorMessage(e)}`;
}
